/**
 * EmailPassword - マイページ「メールアドレス・パスワード」セクション
 *
 * - メールアドレス: blur（フォーカスアウト）で確定して PATCH /api/me に保存。
 *   編集途中の中間文字列が保存されないよう、デバウンス自動保存はやめた。
 *   BE のバリデーションエラー（登録済みメールアドレス等）は入力欄の下に表示する。
 * - パスワード: モーダルで「現在のパスワード」を必須入力し、
 *   POST /api/me/password（changePassword mutation）で変更する。
 *   BE 側で現在パスワードの照合と Django パスワードバリデーションが走り、
 *   成功時は再発行された DRF Token が Cookie に保存し直される
 *   （mutation の onQueryStarted 内で処理）。
 * - BE のバリデーションエラー（現在パスワード誤り・強度不足・確認不一致）は
 *   各入力欄の error にそのまま表示する。
 */
import React, { useState, useEffect } from "react";
import {
  useGetCurrentUserQuery,
  useUpdateCurrentUserMutation,
  useChangePasswordMutation,
} from "../../../app/accounts/authuser";

import Button from "../../../components/button/Button";
import GuideImage from "../../../components/guide-image/GuideImage";
import Input from "../../../components/form-item/Input";
import TitleBlock from "../../../components/block/TitleBlock";
import Modal from "../../../components/modal/Modal";
import SvgLoadingAnimation from "../../../components/loading/index";
import StatusIndicator from "../../../components/status-indicator/StatusIndicator";
import type { StatusType } from "../../../components/status-indicator/StatusIndicator";

interface PasswordFieldErrors {
  currentPassword?: string;
  password?: string;
  passwordConfirm?: string;
  general?: string;
}

/**
 * changePassword の RTK Query エラーから入力欄ごとのメッセージを取り出す。
 * BE のエラー形式（docs/api/me.md）:
 *   { status: "error", errors: { current_password: [...], password: [...], password_confirm: [...] }, message?: string }
 *   { status: "error", message: "..." }（パスワード未設定アカウント等）
 */
const extractPasswordErrors = (err: unknown): PasswordFieldErrors => {
  const data = (err as { data?: unknown })?.data as
    | {
        errors?: Record<string, string[] | string>;
        message?: string;
      }
    | undefined;

  if (!data) {
    return { general: "パスワードの更新に失敗しました。通信環境をご確認ください。" };
  }

  const firstOf = (value: string[] | string | undefined): string | undefined => {
    if (Array.isArray(value)) return value[0];
    return value || undefined;
  };

  const errors = data.errors ?? {};
  const result: PasswordFieldErrors = {
    currentPassword: firstOf(errors.current_password),
    password: firstOf(errors.password),
    passwordConfirm: firstOf(errors.password_confirm),
  };

  if (
    !result.currentPassword &&
    !result.password &&
    !result.passwordConfirm
  ) {
    result.general =
      firstOf(errors.non_field_errors) ??
      data.message ??
      "パスワードの更新に失敗しました。";
  }
  return result;
};

/**
 * updateCurrentUser（PATCH /api/me）の RTK Query エラーから、メールアドレス
 * 入力欄に表示するメッセージを取り出す。
 * BE のエラー形式（MeAuthUserSerializer のバリデーションエラー）:
 *   { status: "error", errors: { email: [...] }, message?: string }
 * 登録済みメールアドレス（unique 制約）や形式・文字数エラーがここに乗る。
 */
const extractEmailError = (err: unknown): string => {
  const data = (err as { data?: unknown })?.data as
    | {
        errors?: Record<string, string[] | string>;
        message?: string;
      }
    | undefined;

  if (!data) {
    return "メールアドレスの保存に失敗しました。通信環境をご確認ください。";
  }

  const emailErrors = data.errors?.email;
  if (Array.isArray(emailErrors) && emailErrors[0]) return emailErrors[0];
  if (typeof emailErrors === "string" && emailErrors) return emailErrors;
  return data.message || "メールアドレスの保存に失敗しました。";
};

const EmailPassword: React.FC = () => {
  const { data: userData, isLoading, error } = useGetCurrentUserQuery();
  const [updateCurrentUser] = useUpdateCurrentUserMutation();
  const [changePassword, { isLoading: isChangingPassword }] =
    useChangePasswordMutation();

  const [formData, setFormData] = useState<{ email?: string }>({});
  const [updateStatus, setUpdateStatus] = useState<StatusType>("idle");
  const [emailError, setEmailError] = useState<string | undefined>(undefined);

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState<PasswordFieldErrors>({});

  // ユーザーデータの初期化
  useEffect(() => {
    if (userData?.data) {
      setFormData({
        email: userData.data.email || "",
      });
    }
  }, [userData]);

  // 入力中はクライアント側の形式チェックだけ行う（保存は blur で確定）
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setFormData((prev) => ({ ...prev, email: value }));
    setEmailError(
      value && !/\S+@\S+\.\S+/.test(value)
        ? "有効なメールアドレスを入力してください。"
        : undefined,
    );
  };

  // blur で確定保存。BE のバリデーションエラー（登録済みメールアドレス等）は
  // 入力欄の下に表示する。
  const handleEmailBlur = async () => {
    const value = (formData.email || "").trim();
    const savedEmail = userData?.data?.email || "";
    if (value === savedEmail) return; // 変更なしなら何もしない

    if (!value) {
      setEmailError("メールアドレスを入力してください。");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(value)) {
      // 形式エラーは handleEmailChange で表示済み。保存しない。
      return;
    }

    try {
      setUpdateStatus("saving");
      await updateCurrentUser({ email: value }).unwrap();
      setFormData((prev) => ({ ...prev, email: value }));
      setEmailError(undefined);
      setUpdateStatus("saved");
      setTimeout(() => setUpdateStatus("idle"), 1000);
    } catch (err) {
      setEmailError(extractEmailError(err));
      setUpdateStatus("error");
      setTimeout(() => setUpdateStatus("idle"), 3000);
    }
  };

  const closePasswordModal = () => {
    setIsPasswordModalOpen(false);
    setCurrentPassword("");
    setPassword("");
    setConfirmPassword("");
    setPasswordErrors({});
  };

  // パスワード変更処理
  const handlePasswordChange = async () => {
    // 送信前のクライアント側チェック（BE でも同じ検証が走る）
    const clientErrors: PasswordFieldErrors = {};
    if (!currentPassword) {
      clientErrors.currentPassword = "現在のパスワードを入力してください。";
    }
    if (password.length < 8) {
      clientErrors.password = "パスワードは8文字以上で入力してください。";
    }
    if (password !== confirmPassword) {
      clientErrors.passwordConfirm = "パスワードが一致しません。";
    }
    if (
      clientErrors.currentPassword ||
      clientErrors.password ||
      clientErrors.passwordConfirm
    ) {
      setPasswordErrors(clientErrors);
      return;
    }

    try {
      setUpdateStatus("saving");
      await changePassword({
        current_password: currentPassword,
        password,
        password_confirm: confirmPassword,
      }).unwrap();
      closePasswordModal();
      setUpdateStatus("saved");
      setTimeout(() => setUpdateStatus("idle"), 1000);
    } catch (err) {
      setPasswordErrors(extractPasswordErrors(err));
      setUpdateStatus("error");
      setTimeout(() => setUpdateStatus("idle"), 3000);
    }
  };

  if (isLoading) return <SvgLoadingAnimation />;
  if (error) return <div>Error: データの取得に失敗しました</div>;

  return (
    <>
      <TitleBlock
        title="メールアドレス・パスワード"
        description="サインインに必要なメールアドレスとパスワードを変更"
      />
      <div className="u-col u-gap16">
        <GuideImage name="account" />
        <div className="u-w100">
          <Input
            label="メールアドレス"
            option="required"
            type="email"
            name="email"
            inputmode="email"
            placeholder="username@soremo.jp"
            value={formData.email || ""}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            error={emailError}
          />
        </div>
        <Button
          theme="secondary"
          size="medium"
          variant="filled"
          onClick={() => setIsPasswordModalOpen(true)}
        >
          パスワード変更
        </Button>
        <StatusIndicator status={updateStatus} withBottomAppBar={false} />
        {isPasswordModalOpen && (
          <Modal
            title="パスワード変更"
            onClose={closePasswordModal}
            primaryAction={{
              label: "更新",
              onClick: handlePasswordChange,
              loading: isChangingPassword,
              disabled: isChangingPassword,
            }}
          >
            <div className="u-w100 u-col u-gap16">
              <Input
                label="現在のパスワード"
                option="required"
                type="password"
                autocomplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                error={passwordErrors.currentPassword}
              />
              <Input
                label="新しいパスワード"
                option="required"
                type="password"
                autocomplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={passwordErrors.password}
              />
              <Input
                label="新しいパスワード（確認）"
                option="required"
                type="password"
                autocomplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={passwordErrors.passwordConfirm}
              />
              {passwordErrors.general && (
                <p className="bodytext-13 u-text-green">
                  {passwordErrors.general}
                </p>
              )}
            </div>
          </Modal>
        )}
      </div>
    </>
  );
};

export default EmailPassword;
